// @flow
import firebase from "firebase";
import User from "../models/user";
import Town from "../models/town";
import Team from "../models/team";
import TeamMember from "../models/team-member";
import TrashDrop from "../models/trash-drop";
import Permission from "../models/permission";
import Message from "../models/message";
import Admin from "../models/admin";
import EventInfo from "../models/event-info";
import Celebration from "../models/celebration";
import TrashCollectionSite from "../models/trash-collection-site";
import SupplyDistributionSite from "../models/supply-distribution-site";
import { firebaseConfig, baseURL } from "./firebase-config";
import "firebase/firestore";
import * as R from "ramda";
import FAQ from "../models/faq";

firebase.initializeApp(firebaseConfig);

// Initialize Cloud Firestore through Firebase
const db = firebase.firestore();

const createProfile = (profile: Object) => User.create(profile);

const deconstruct = obj => JSON.parse(JSON.stringify(obj));

const errorMessages = {
    400: "Bad Request",
    401: "Unauthorized",
    403: "Forbidden",
    404: "Not Found",
    500: "Internal Server Error"
};

/**
 * Converts a POJO to a URL query string
 * @param {object} data - queries in JSON form {foo: 'bar', bah: 'baz'}
 * @returns {string} - string in the form ?foo=bar&bah=baz
 */
const json2URLParams = data => {
    const entries = Object
        .entries(data || {})
        .filter(entry => entry[1] !== null && entry[1] !== "" && typeof (entry[1]) !== "undefined");
    const params = entries.map(entry => `${ encodeURIComponent(entry[0]) }=${ encodeURIComponent(entry[1]) }`).join("&");
    return entries.length > 0 ? `?${ params }` : "";
};


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function usersListeners(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("profiles")
        .onSnapshot(querySnapshot => {
            const data = {};
            querySnapshot.forEach(doc => {
                data[doc.id] = User.create({ ...doc.data(), uid: doc.id });
            });
            onSuccess(data);
        }, (error: Object) => {
            onFail(error);
        });
}


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function celebrationsListener(onSuccess: ( Object=> any), onFail: (Object => any)) {
    return db.collection("celebrations")
        .onSnapshot(querySnapshot => {
            const data = {};
            querySnapshot.forEach(doc => {
                data[doc.id] = Celebration.create({ ...doc.data(), id: doc.id });
            });
            onSuccess(data);
        }, (error: Object) => {
            onFail(error);
        });
}


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function supplyDistributionSitesListener(onSuccess: ( Object=> any), onFail: (Object => any)) {
    return db.collection("supplyDistributionSites")
        .onSnapshot(querySnapshot => {
            const data = {};
            querySnapshot.forEach(doc => {
                data[doc.id] = SupplyDistributionSite.create({ ...doc.data(), id: doc.id });
            });
            onSuccess(data);
        }, (error: Object) => {
            onFail(error);
        });
}

/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function trashCollectionSitesListener(onSuccess: ( Object=> any), onFail: (Object => any)) {
    return db.collection("trashCollectionSites")
        .onSnapshot(querySnapshot => {
            const data = {};
            querySnapshot.forEach(doc => {
                data[doc.id] = TrashCollectionSite.create({ ...doc.data(), id: doc.id });
            });
            onSuccess(data);
        }, (error: Object) => {
            onFail(error);
        });
}


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function townsListener(onSuccess: ( Object=> any), onFail: (Object => any)) {
    return db.collection("towns")
        .onSnapshot(querySnapshot => {
            const data = {};
            querySnapshot.forEach(doc => {
                data[doc.id] = Town.create({ ...doc.data(), id: doc.id });
            });
            onSuccess(data);
        }, (error: Object) => {
            onFail(error);
        });
}

/**
 * @private
 * @param {function} - listener callback
 * @param {function} - listener callback
 * @param {string} id - Team Id
 * @returns {Promise<void>} - Promise
 */
const teamMemberListener = R.curry((onSuccess: ( Object=> any), onFail: ( Object=> any), id: string) => db.collection(`teams/${ id }/members`)
    .onSnapshot(querySnapshot => {        
        const members = querySnapshot.docs.reduce((obj, member) => ({
            ...obj,
            [member.id]: TeamMember.create(member.data(), member.id)
        }), {});
        onSuccess({ id, members });
    }, (error: Object) => {
        onFail({ id, error });
    }));


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @param {function} myTeamMemberListener - curried teamMember Listener
 * @returns {Promise<void>} - Promise
 */
const teamsListener = (onSuccess: ( Object=> any), onFail: ( Object=> any), myTeamMemberListener) => db.collection("teams")
    .onSnapshot(querySnapshot => {
        const data = {};
        querySnapshot.forEach(doc => {
            data[doc.id] = Team.create(doc.data(), doc.id);
            myTeamMemberListener(doc.id);
            
        });
        onSuccess(data);
    }, error => {
        onFail(error);
    });


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function permissionListener(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("admins")
        .onSnapshot(querySnapshot => {
            const data = {};
            querySnapshot.forEach(doc => {
                data[doc.id] = Permission.create(doc.data(), doc.id);
            });
            onSuccess(data);
        }, error => {
            onFail(error);
        });
}

/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function messageListener(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("messages/")
        .onSnapshot(querySnapshot => {
                const data = {};
                querySnapshot.forEach(doc => {
                    data[doc.id] = Message.create(doc.data(), doc.id);
                });
                onSuccess(data);
            }, error => {
                onFail(error); // TODO: Make this a FAIL action
            }
        );
}

/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function trashDropListener(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("trashDrops")
        .onSnapshot(querySnapshot => {
                const data = {};
                querySnapshot.forEach(doc => {
                    data[doc.id] = TrashDrop.create(doc.data(), doc.id);
                });
                onSuccess(data);
            }, error => {
                onFail(error); // TODO: Make this a FAIL action
            }
        );
}


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function eventInfoListener(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("eventInfo")
        .onSnapshot(querySnapshot => {
                const doc = querySnapshot.docs.find(doc => doc.id === "settings");
                const docObject = doc ? { id: doc.id, ...doc.data() } : {};
                const data = EventInfo.create(docObject);
                onSuccess(data);
            }, error => {
                onFail(error);
            }
        );
}

/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function faqsListener(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("faqs")
        .onSnapshot(querySnapshot => {
                const data = {};
                querySnapshot.forEach(doc => {
                    data[doc.id] = FAQ.create(doc.data(), doc.id);
                });
                onSuccess(data);

            }, error => {
                onFail(error);
            }
        );
}


/**
 * @private
 * @param {function} onSuccess - listener callback
 * @param {function} onFail - listener callback
 * @returns {Promise<void>} - Promise
 */
function adminsListener(onSuccess: ( Object=> any), onFail: ( Object=> any)) {
    return db.collection("admins")
        .onSnapshot(querySnapshot => {
                const data = {};
                querySnapshot.forEach(doc => {
                    data[doc.id] = Admin.create(doc.data(), doc.id);
                });
                onSuccess(data);
            }, error => {
                onFail(error); // TODO: Make this a FAIL action
            }
        );
}

const isAdmin = (uid: string): Promise<*> => (
    new Promise((resolve, reject) => {
        const docRef = db.collection("admins").doc(uid);
        docRef.get().then(doc => {
            if (doc.exists) {
                resolve(true);
            } else {
                reject(false);
            }
        }).catch(error => {
            reject(error);
        });
    }));


type InitConfigType = {
    onLogin: User => void,
    onLogout: () => void,
    userFailedAuthentication: any => void,
    onAdminFetchSuccess: any => void,
    onCelebrationsFetchSuccess: any => void,
    onEventFetchSuccess: any => void,
    onFAQFetchSuccess: any => void,
    onSupplyDistributionSitesFetchSuccess: any => void,
    onTrashCollectionSitesFetchSuccess: any => void,
    onUsersFetchSuccess: any => void,
    onTownsFetchSuccess: any => void,
    onTeamFetchSuccess: any => void,
    onTeamMembersFetchFail: string => void,
    onTeamMembersFetchSuccess: string => void,
    onTrashDropFetchSuccess: any => void,
    onMessageFetchSuccess: any => void,
    onPermissionFetchSuccess: any => void,
    onAdminFetchFail: any => void,
    onCelebrationsFetchFail: any => void,
    onEventFetchFail: any => void,
    onFAQFetchFail: any => void,
    onSupplyDistributionSitesFetchFail: any => void,
    onUsersFetchFail: any => void,
    onTownsFetchFail: any => void,
    onTeamsFetchFail: any => void,
    onTrashCollectionSitesFetchFail: any => void,
    onTrashDropFetchFail: any => void,
    onMessageFetchFail: any => void,
    onPermissionFetchFail: any => void,
    onInitSuccess: Array<any> => void,
    onInitFail: Array<any> => void
};

/**
 * Initializes the application
 * @returns {void}
 */
export function initialize(config: InitConfigType) {
    return new Promise(
        (resolve, reject) => {
            try {
                firebase.auth().onAuthStateChanged(user => {
                    if (Boolean(user)) {
                        isAdmin(user.uid)
                            .then(() => {
                                const { uid, email, displayName, photoURL } = user;
                                db.collection("profiles").doc(uid).get().then(
                                    doc => {
                                        if (!doc.exists) {
                                            createProfile({ uid, email, displayName, photoURL });
                                        }
                                    }).catch((error) => {
                                    console.log("Error getting profile document:", error); // TODO: Make this a FAIL action
                                });
                                Promise.all([
                                    adminsListener(config.onAdminFetchSuccess, config.onAdminFetchFail),
                                    eventInfoListener(config.onEventFetchSuccess, config.onEventFetchFail),
                                    faqsListener(config.onFAQFetchSuccess, config.onFAQFetchFail),
                                    usersListeners(config.onUsersFetchSuccess, config.onUsersFetchFail),
                                    townsListener(config.onTownsFetchSuccess, config.onTownsFetchFail),
                                    teamsListener(config.onTeamFetchSuccess, config.onTeamsFetchFail, teamMemberListener(config.onTeamMembersFetchSuccess, config.onTeamMembersFetchFail)),
                                    trashDropListener(config.onTrashDropFetchSuccess, config.onTrashDropFetchFail),
                                    messageListener(config.onMessageFetchSuccess, config.onMessageFetchFail),
                                    permissionListener(config.onPermissionFetchSuccess, config.onPermissionFetchFail),
                                    celebrationsListener(config.onCelebrationsFetchSuccess, config.onCelebrationsFetchFail),
                                    supplyDistributionSitesListener(config.onSupplyDistributionSitesFetchSuccess, config.onSupplyDistributionSitesFetchFail),
                                    trashCollectionSitesListener(config.onTrashCollectionSitesFetchSuccess, config.onTrashCollectionSitesFetchFail)
                                ])
                                    .then(() => {
                                        config.onLogin(user);
                                    })
                                    .catch(error => {
                                        console.log(error)
                                        config.userFailedAuthentication(error);

                                    });
                            })
                            .catch(error => {
                                console.log(error)
                                config.userFailedAuthentication("You are not authorized to access this system");
                            });
                    } else {
                        config.onLogout();
                    }
                });
                resolve(true);
            } catch (error) {
                reject(error);
            }
        }
    );
}


/**
 * Logs a user in using an email and password
 * @param {string} _email - the user's email address
 * @param {string} password - password - the users password
 * @returns {Promise<firebase.auth.UserCredential | {type, error}>}  - Promise
 */
export function loginWithEmailPassword(_email: string, password: string) {
    return firebase
        .auth()
        .signInWithEmailAndPassword(_email, password);
}

/**
 *
 * @param {string} emailAddress - Email Address
 * @returns {Promise<void>} - Promise
 */
export function resetPassword(emailAddress: string) {
    return firebase.auth().sendPasswordResetEmail(emailAddress); // TODO: Handle FAIL
}

/**
 *  Logs a user out.
 * @returns {Promise<void>}  - Promise
 */
export function logout() {
    return firebase.auth().signOut(); // TODO: Handle Fail
}

/** *************** MESSAGING *************** **/

/**
 * sends a direct message to a user
 * @param {string} uid - user id
 * @param {string} message - message
 * @returns {Promise<firebase.firestore.DocumentReference>} - Promise
 */
export function sendUserMessage(uid: string, message: Object) {
    return db.collection(`messages/${ uid }/messages`).add(deconstruct(message))
        .then(() => {
            console.log("Message successfully added!");
        })
        .catch(error => {
            // The document probably doesn't exist.
            console.error("Error adding Message: ", error); // TODO: Make this a FAIL action

        });

}

/**
 * send a message to a group of users
 * @param {Array<string>} group - array of user id's
 * @param {string} message - message to send
 * @returns {Promise<any[]>} - Promise
 */
export function sendGroupMessage(group: Array<string>, message: Object) {
    return Promise.all(group.map((memberUID) => {
        sendUserMessage(memberUID, message);
    })); // TODO: Handle Fail
}

/**
 * updates a user message
 * @param {string} message - updated message
 * @param {string} uid - user id
 * @returns {Promise<void>} - Promise
 */
export function updateMessage(message: Object, uid: string) {
    const newMessage = { ...message, sender: { ...message.sender } };
    return db.collection(`messages/${ uid }/messages`)
        .doc(message.id)
        .set(newMessage)
        .then(() => {
            console.log("Message successfully set!");
        })
        .catch(error => {
            // The document probably doesn't exist.
            console.error("Error setting Message: ", error); // TODO: Make this a FAIL action

        });

}

/**
 * updates a town
 * @param {Object} town - town object
 * @param {string} [id] - id of town
 * @returns {Promise<void>} - Promise
 */
export function updateTown(town: Object, id?: string) {
    const newTown = deconstruct(town);
    Reflect.deleteProperty(newTown, "create");
    newTown.updated = new Date();
    return db.collection("towns")
        .doc(id || town.id)
        .update(newTown).then(() => {
            console.log("Town successfully updated!");
        })
        .catch(error => {
            // The document probably doesn't exist.
            console.error("Error updating Town: ", error); // TODO: Make this a FAIL action

        });
}

/**
 * Deletes a message
 * @param {string} uid - user's id
 * @param {string} messageId - message id
 * @returns {Promise<void>} - Promise
 */
export function deleteMessage(uid: string, messageId: string) {
    return db.collection(`messages/${ uid }/messages`)
        .doc(messageId)
        .delete()
        .then(() => {
            console.log("Message successfully deleted!");
        })
        .catch(error => {
            // The document probably doesn't exist.
            console.error("Error deleting Message: ", error); // TODO: Make this a FAIL action

        });
}


/**
 * Updates a user
 * @param {Object} user - User object
 * @param {string} [uid] -user's id
 * @returns {Promise<void>} - Promise
 */
export async function updateUser(user: Object, uid?: string) {
    const newUser = deconstruct(user);
    Reflect.deleteProperty(newUser, "created");
    newUser.updated = new Date();
    return db.collection("profiles")
        .doc(uid || user.uid)
        .update(newUser)
        .then(() => {
            console.log("User successfully updated!");
        })
        .catch(error => {
            // The document probably doesn't exist.
            console.error("Error updating User: ", error); // TODO: Make this a FAIL action

        });
}

/**
 * Updates an admin record
 * @param {string} [uid] - user's id
 * @param {Object} record - admin record
 * @returns {Promise<void>} - Promise
 */
export function updateAdmin(uid: string, record: Object) {
    const docRef = db.collection("admins").doc(uid);
    return docRef.get().then(
        docSnapshot =>
            docSnapshot.exists
                ? docRef.update(record)
                    .then(() => {
                        console.log("Admins successfully updated!");
                    })
                    .catch(error => {
                        // The document probably doesn't exist.
                        console.error("Error updating Admins: ", error); // TODO: Make this a FAIL action

                    })
                : docRef.set(record).then(() => {
                    console.log("Admins successfully setup!");
                })
                    .catch(error => {
                        // The document probably doesn't exist.
                        console.error("Error setting Admins: ", error); // TODO: Make this a FAIL action

                    })
    );
}

/**
 * updates a team
 * @param {Object} team - team object
 * @param {string} [id] - id of team
 * @returns {Promise<void>} - Promise
 */
export function updateTeam(team: Object, id?: string) {
    const newTeam = deconstruct(team);
    Reflect.deleteProperty(newTeam, "created");
    newTeam.updated = new Date();
    return db.collection("teams")
        .doc(id || team.id)
        .update(newTeam);
}

/**
 * updates a trashDrop
 * @param {Object} trashDrop - trashDrop object
 * @param {string} [id] - id of trashDrop
 * @returns {Promise<void>} - Promise
 */
export function updateTrashDrop(trashDrop: Object, id?: string) {
    const newTrashDrop = deconstruct(trashDrop);
    Reflect.deleteProperty(newTrashDrop, "created");
    newTrashDrop.updated = new Date();
    return db.collection("trashDrops")
        .doc(id || trashDrop.id)
        .update(newTrashDrop).then(() => {
            console.log("TrashDrop successfully updated!");
        })
        .catch(error => {
            // The document probably doesn't exist.
            console.error("Error updating TrashDrop: ", error);
        });
}


/**
 * Updates Green Up Day Event
 * @param {Object} eventInfo - Event object
 * @returns {Promise<void>} - Promise
 */
export async function updateEvent(eventInfo: Object) {
    const newEventInfo = deconstruct(eventInfo);
    Reflect.deleteProperty(newEventInfo, "created");
    newEventInfo.updated = new Date();
    const doc = db.collection("eventInfo").doc("settings");

    return new Promise((resolve, reject) => {
        doc.update(newEventInfo)
            .then(() => {
                resolve(newEventInfo);
            })
            .catch(() => {
                const createdEvent = { ...newEventInfo, created: new Date() };
                return doc.set(createdEvent)
                    .then(() => {
                        resolve(createdEvent);
                    })
                    .catch(e => {
                        reject(e);
                    });
            });
    });
}

// REST API ENDPOINTS


/**
 * Send a Get call the Firebase server
 * @param url
 * @returns {function(*=): any}
 */
const getData = (url = "") => async (config) => {
    const token = await getToken(); // TODO: Dupe code in patchData func.Let's abstract this (JN)
    const urlParts = url.split("/");
    const params = urlParts.filter(part => part.startsWith(":")).map(part => part.replace(":", ""));
    const queries = R.compose(R.fromPairs, R.filter(entry => !params.includes(entry[0])), Object.entries)(config);
    const _url = R.compose(R.join("/"), R.filter(part => Boolean(part)), R.map(part => part.startsWith(":") ? config[part.replace(/[^a-zA-Z0-9]/gi, "")] : part))(urlParts);
    const myURL = `${ baseURL }/${ _url }${ json2URLParams(queries) }`;
    const response = await fetch(myURL, {
        method: "GET",
        mode: "cors", // no-cors, cors, *same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ token }`
        },
        redirect: "follow", // manual, *follow, error
        referrer: "no-referrer" // no-referrer, *client
    });
    if (!response.ok) {
        const { status, statusText } = response;
        throw new Error({ status, message: statusText });
    }
    return await response.json(); // parses JSON response into native JavaScript objects
};


const toFormUrlEncoded = (json: Object) => Object.entries(json)
    .map(([key, value]) => `${ encodeURIComponent(key) }=${ encodeURIComponent(JSON.stringify(value)) }`)
    .join("&");

/**
 * Gets the current user's Firebase token
 * @returns {Promise<string>}
 */
export const getToken = async () => await firebase.auth().currentUser ? firebase.auth().currentUser.getIdToken() : Promise.reject("no current user");

/**
 * Sends a Patch call to the Firebase server
 * @param {string } url - API url
 * @returns {function(*=): any}
 */
const postData = (url = "") => async (config) => {
    const token = await getToken();
    const urlParts = url.split("/");
    const params = urlParts.filter(part => part.startsWith(":")).map(part => part.replace(":", ""));
    const body = R.compose(R.fromPairs, R.filter(entry => !params.includes(entry[0])), Object.entries)(config);
    const _url = R.compose(R.join("/"), R.filter(part => Boolean(part)), R.map(part => part.startsWith(":") ? config[part.replace(/[^a-zA-Z0-9]/gi, "")] : part))(urlParts);
    const myURL = `${ baseURL }/${ _url }`;
    const response = await fetch(myURL, {
        method: "POST",
        mode: "cors", // no-cors, cors, *same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ token }`
        },
        body: JSON.stringify(body),
        redirect: "follow", // manual, *follow, error
        referrer: "no-referrer" // no-referrer, *client
    });
    if (!response.ok) {
        const { status, statusText } = response;
        throw new Error({ status, message: statusText });
    }
    const myData = await response.json(); // parses JSON response into native JavaScript objects
    return myData;
};

/**
 * Sends a Patch call to the Firebase server
 * @param {string } url - API url
 * @returns {function(*=): any}
 */
const patchData = (url = "") => async (config) => {
    const token = await getToken();
    const urlParts = url.split("/");
    const params = urlParts.filter(part => part.startsWith(":")).map(part => part.replace(":", ""));
    const body = R.compose(R.fromPairs, R.filter(entry => !params.includes(entry[0])), Object.entries)(config);
    const _url = R.compose(R.join("/"), R.filter(part => Boolean(part)), R.map(part => part.startsWith(":") ? config[part.replace(/[^a-zA-Z0-9]/gi, "")] : part))(urlParts);
    const myURL = `${ baseURL }/${ _url }`;
    const response = await fetch(myURL, {
        method: "PATCH",
        mode: "cors", // no-cors, cors, *same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ token }`
        },
        body: JSON.stringify(body),
        redirect: "follow", // manual, *follow, error
        referrer: "no-referrer" // no-referrer, *client
    });
    if (!response.ok) {
        const { status, statusText } = response;
        throw new Error({ status, message: statusText });
    }
    const myData = await response.json(); // parses JSON response into native JavaScript objects
    return myData;
};


/**
 * Sends a Put call to the Firebase server
 * @param {string } url - API url
 * @returns {function(*=): any}
 */
const putData = (url = "") => async (data) => {
    const token = await getToken();
    const urlParts = url.split("/");
    const params = urlParts.filter(part => part.startsWith(":")).map(part => part.replace(":", ""));
    const prunedData = R.compose(R.fromPairs, R.filter(entry => !params.includes(entry[0])), Object.entries)(data);
    const body = JSON.stringify(prunedData);
    const _url = R.compose(R.join("/"), R.filter(part => Boolean(part)), R.map(part => part.startsWith(":") ? data[part.replace(/[^a-zA-Z0-9]/gi, "")] : part))(urlParts);
    const myURL = `${ baseURL }/${ _url }`;

    const config = {
        method: "PUT",
        mode: "cors", // no-cors, cors, *same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ token }`
        },
        body,
        redirect: "follow", // manual, *follow, error
        referrer: "no-referrer" // no-referrer, *client
    };
    const response = await fetch(myURL, config);
    if (!response.ok) {
        const { status, statusText } = response;
        throw new Error(statusText || errorMessages[status]);
    }
    const myData = await response.json(); // parses JSON response into native JavaScript objects
    return myData;
};

/**
 * Sends a Delete call to the Firebase server
 * @param {string } url - API url
 * @returns {function(*=): any}
 */
const deleteData = (url = "") => async (config) => {
    const token = await getToken();
    const urlParts = url.split("/");
    const params = urlParts.filter(part => part.startsWith(":")).map(part => part.replace(":", ""));
    const body = R.compose(R.fromPairs, R.filter(entry => !params.includes(entry[0])), Object.entries)(config);
    const _url = R.compose(R.join("/"), R.filter(part => Boolean(part)), R.map(part => part.startsWith(":") ? config[part.replace(/[^a-zA-Z0-9]/gi, "")] : part))(urlParts);
    const myURL = `${ baseURL }/${ _url }`;
    const response = await fetch(myURL, {
        method: "DELETE",
        mode: "cors", // no-cors, cors, *same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ token }`
        },
        body: JSON.stringify(body),
        redirect: "follow", // manual, *follow, error
        referrer: "no-referrer" // no-referrer, *client
    });
    if (!response.ok) {
        const { status, statusText } = response;
        throw new Error({ status, message: statusText });
    }
    const myData = await response.json(); // parses JSON response into native JavaScript objects
    return myData;
};

/**
 * Sends a reset email
 * @param {string} email - user's registered email
 * @returns {Promise<*>}
 */
export const sendPasswordResetEmail = async (email) => await firebase.auth().sendPasswordResetEmail(email);

/**
 * Sign in a user using email and password
 * @param {string} email - user's registered email
 * @param {string} password - user's password
 * @returns Promise<Object>
 */
export const signInWithEmailPassword = async (email, password) => firebase.auth().signInWithEmailAndPassword(email, password);

/**
 * Logs the current user out
 * @returns {Promise<*>}
 */
export const signOut = () => firebase.auth().signOut();

/**
 * Returns the currently logged in user
 * @returns {firebase.User}
 */
export const currentUser = () => firebase.auth().currentUser;

/**
 * Adds a callback the authStateChanged event.
 * @param {function} callback - callback invoked on event
 * @returns {firebase.Unsubscribe} - key to unsubscribe
 */
export const onAuthStateChanged = (callback: Object => any) => firebase.auth().onAuthStateChanged(callback);

/**
 *
 * @type {function(*=): any}
 */
export const apiSayHello = getData("hello");

/** ** TOWNS ****/

/** Content-Encoding
 *
 * @type {function(*=): any}
 */
export const queryTowns = getData("towns/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiUpdateTown = patchData("towns/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiSetTowns = putData("towns");


/** **  TRASH COLLECTION LOCATIONS ****/
/**
 *
 * @type {function(*=): any}
 */
export const apiGetTrashCollectionSites = getData("trash_collection_sites/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiCreateTrashCollectionSite = postData("trash_collection_sites");

/**
 *
 * @type {function(*=): any}
 */
export const updateTrashCollectionSite = patchData("trash_collection_sites/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiSetTrashCollectionSites = putData("trash_collection_sites");

/**
 *
 * @type {function(*=): any}
 */
export const apiDeleteTrashCollectionSite = deleteData("trash_collection_sites/:id");

/** ** SUPPLY DISTRIBUTION LOCATIONS ****/

/**
 *
 * @type {function(*=): any}
 */
export const apiGetSupplyDistributionSites = getData("supply_distribution_sites/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiCreateSupplyDistributionSite = postData("supply_distribution_sites");

/**
 *
 * @type {function(*=): any}
 */
export const apiUpdateSupplyDistributionSite = patchData("supply_distribution_sites/:id");

/**
 *
 * @type {function(*=): any}
 */
export const setSupplyDistributionSites = putData("supply_distribution_sites");

/**
 *
 * @type {function(*=): any}
 */
export const apiDeleteSupplyDistributionSite = deleteData("supply_distribution_sites/:id");

/** **  CELEBRATIONS ****/

/**
 *
 * @type {function(*=): any}
 */
export const apiGetCelebrations = getData("celebrations/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiCreateCelebration = postData("celebrations");

/**
 *
 * @type {function(*=): any}
 */
export const apiUpdateCelebration = patchData("celebrations/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiSetCelebrations = putData("celebrations");

/**
 *
 * @type {function(*=): any}
 */
export const apiDeleteCelebration = deleteData("celebrations/:id");

/** **  FAQS  ****/

/**
 *
 * @type {function(*=): any}
 */
export const apiGetFAQs = getData("faqs/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiCreateFAQ = postData("faqs");

/**
 *
 * @type {function(*=): any}
 */
export const apiUpdateFAQ = patchData("faqs/:id");

/**
 *
 * @type {function(*=): any}
 */
export const apiSetFAQs = putData("faqs");

/**
 *
 * @type {function(*=): any}
 */
export const apiDeleteFAQ = deleteData("faqs/:id");


export const baseUrl = baseURL;
